import React, { useState, useEffect } from 'react';
import { Box, SimpleGrid, Flex, Button, Spinner, Badge, Text, HStack, Spacer, IconButton, Center } from '@chakra-ui/react';
import moment from 'moment';
import { IoMdTime } from "react-icons/io";
import { FaShoppingCart } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { fetchTimeSlots } from '../apis/timeSlotsApi';
import { handleCheckout } from '../apis/checkoutApi';
import { RiArrowRightLine, RiMailLine } from "react-icons/ri";
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

const TimeSlots = ({ selectedDate, selectedBay }) => {
  const [times, setTimes] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedDate && selectedBay) {  
      const selectedDateISO = new Date(selectedDate).toISOString().split('T')[0];  
      setIsLoading(true);
      setSelectedTimes([]);
      // Reset page to 0 when date or bay changes
      setPage(0);
  
      fetchTimeSlots(selectedDateISO, selectedBay)
        .then(response => {
          const isToday = moment().isSame(moment(selectedDateISO), 'day');
          const currentHour = isToday ? moment().startOf('hour').hour() : 0;
          const fetchedTimes = response.data
            .filter(time => time.hour >= currentHour)
            .map(time => {
              const start = moment({hour: time.hour}).format('h:mmA');
              const end = moment({hour: time.hour}).add(1, 'hour').format('h:mmA');
              return {
                time: `${start}-${end}`,
                hour: time.hour,
                isAvailable: time.isavailable // Corrected property name
              };
            });
          setTimes(fetchedTimes);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error:', error);
          setIsLoading(false);
        });
    }
  }, [selectedDate, selectedBay]);

  const handleTimeClick = (hour) => {
    if (selectedTimes.includes(hour)) {
      const newSelectedTimes = selectedTimes.filter(h => h !== hour);
      setSelectedTimes(newSelectedTimes);
    } else {
      const newSelectedTimes = [...selectedTimes, hour];
      setSelectedTimes(newSelectedTimes);
    }
  };

  const handleNext = () => {
    if ((page + 1) * itemsPerPage < times.length) {
      setPage(page + 1);
    }
  };

  const handlePrevious = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const paginatedTimes = times.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  
  // Get the time range for the current page
  const getTimeRangeLabel = () => {
    if (times.length === 0) return "No times available";
    if (paginatedTimes.length === 0) {
      // If we're on a page with no times, reset to page 0
      setPage(0);
      return "Loading times...";
    }
    
    const firstTime = paginatedTimes[0].time.split('-')[0];
    const lastTime = paginatedTimes[paginatedTimes.length - 1].time.split('-')[1];
    
    return `${firstTime} - ${lastTime}`;
  };

  const handleCartClick = () => {
    const formattedTimes = selectedTimes.map(hour => {
      const start = moment(selectedDate).set({ hour, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss');
      const end = moment(selectedDate).set({ hour, minute: 0, second: 0 }).add(1, 'hour').format('YYYY-MM-DD HH:mm:ss');
      return {
        name: `${moment({ hour }).format('hA')}-${moment({ hour }).add(1, 'hour').format('hA')}`,
        price: 50,
        bay: selectedBay,
        date: selectedDate,
        start_time: start,
        end_time: end
      };
    });

    handleCheckout(formattedTimes, selectedBay, selectedDate)
      .then(response => {
        // Navigate to checkout and ensure we scroll to top
        navigate('/checkout');
        // Force scroll to top
        window.scrollTo(0, 0);
      })
      .catch(error => {
        console.error('Error:', error);
      });
  };

  if (isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="200px"
        borderRadius="lg"
        p={4}
        mt={4}
        bg="white"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
      >
        <Spinner color="#82ca9c" size="lg" thickness="3px" />
      </Box>
    );
  }

  return (
    <Box 
      marginBottom="150px" 
      borderRadius="lg"
      p={4}
      mt={4}
      bg="white"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
    >
      <HStack marginBottom={4}>
        <IoMdTime color="#82ca9c" fontSize={20} />
        <Text fontWeight="600">Select available times</Text>
        <Spacer />
        
        {selectedTimes.length > 0 && (
          <Box position="relative">
            <IconButton
              icon={<FaShoppingCart />}
              colorScheme="green"
              variant="ghost"
              size="sm"
              borderRadius="full"
              onClick={handleCartClick}
              aria-label="Checkout"
            />
            <Badge 
              position="absolute" 
              top="-8px" 
              right="-8px" 
              borderRadius="full" 
              bg="#82ca9c" 
              color="white"
              fontSize="0.8em"
              minW="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
            >
              {selectedTimes.length}
            </Badge>
          </Box>
        )}
      </HStack>
      
      {times.length > 0 ? (
        <>
          <HStack justify="space-between" mb={4}>
            <HStack>
              <IconButton 
                icon={<ChevronLeftIcon />} 
                onClick={handlePrevious}
                isDisabled={page === 0}
                size="sm"
                variant="ghost"
                colorScheme="green"
                borderRadius="full"
                h="32px"
                minW="32px"
                p={0}
              />
              <Text fontWeight="medium" px={2}>{getTimeRangeLabel()}</Text>
              <IconButton 
                icon={<ChevronRightIcon />} 
                onClick={handleNext}
                isDisabled={(page + 1) * itemsPerPage >= times.length}
                size="sm"
                variant="ghost"
                colorScheme="green"
                borderRadius="full"
                h="32px"
                minW="32px"
                p={0}
              />
            </HStack>
            
            {selectedTimes.length > 0 && (
              <Button 
                size="sm" 
                colorScheme="green" 
                onClick={handleCartClick}
                rightIcon={<RiArrowRightLine />}
                borderRadius="md"
                boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
                _hover={{
                  boxShadow: "0 4px 8px rgba(130, 202, 156, 0.4)",
                  transform: "translateY(-1px)"
                }}
              >
                Checkout
              </Button>
            )}
          </HStack>
          
          <Box 
            bg="#f8f9fa" 
            borderRadius="md" 
            py={3} 
            px={2}
          >
            <SimpleGrid 
              columns={2} 
              spacing={4} 
              fontSize="sm"
              mx="auto"
              maxW="500px"
            >
              {paginatedTimes.map((timeObj, index) => {
                const hour = moment(timeObj.time, 'h:mm A').hour();
                return (
                  <Box 
                    key={hour}
                    bg={selectedTimes.includes(hour) ? "rgba(130, 202, 156, 0.1)" : "white"} 
                    color={timeObj.isAvailable ? "black" : "gray.400"} 
                    p={4} 
                    borderRadius="lg" 
                    boxShadow={selectedTimes.includes(hour) 
                      ? "0 4px 12px rgba(130, 202, 156, 0.2)" 
                      : timeObj.isAvailable 
                        ? "0 2px 6px rgba(0, 0, 0, 0.05)" 
                        : "none"
                    }
                    onClick={() => timeObj.isAvailable && handleTimeClick(hour)}
                    opacity={timeObj.isAvailable ? 1 : 0.6}
                    cursor={timeObj.isAvailable ? "pointer" : "not-allowed"}
                    transition="all 0.2s"
                    _hover={{
                      boxShadow: timeObj.isAvailable ? "0 6px 12px rgba(0, 0, 0, 0.1)" : "none",
                      transform: timeObj.isAvailable && !selectedTimes.includes(hour) ? "translateY(-3px)" : "none",
                    }}
                    textAlign="center"
                    fontWeight={selectedTimes.includes(hour) ? "bold" : "normal"}
                  >
                    {timeObj.time}
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        </>
      ) : (
        <Center p={8}>
          <Text color="gray.500" fontWeight="medium">No time slots available for this date</Text>
        </Center>
      )}
    </Box>
  );
};

export default TimeSlots;